import {
  Bed,
  BedSingle,
  Building2,
  Clock,
  HandHeart,
  Landmark,
  Lock,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { humanize } from '@/lib/utils';
import type { AgeUnit, BoardStatus } from './bed-status';

export interface StatusLook {
  icon: LucideIcon;
  text: string;
  tile: string;
  edge: string;
  border: string;
  glow: string;
  blob: string;
  ring: string;
  flash: string;
  stroke: string;
}

export const STATUS_LOOK: Record<BoardStatus, StatusLook> = {
  occupied: {
    icon: Bed,
    text: 'text-info',
    tile: 'bg-info/10 text-info ring-info/20',
    edge: 'bg-info',
    border: 'border-info/25',
    glow: 'hover:shadow-info/15',
    blob: 'bg-info/10',
    ring: 'ring-info/60',
    flash: 'border-info',
    stroke: 'stroke-info',
  },
  reserved: {
    icon: Clock,
    text: 'text-chart-3',
    tile: 'bg-chart-3/10 text-chart-3 ring-chart-3/20',
    edge: 'bg-chart-3',
    border: 'border-chart-3/25',
    glow: 'hover:shadow-chart-3/15',
    blob: 'bg-chart-3/10',
    ring: 'ring-chart-3/60',
    flash: 'border-chart-3',
    stroke: 'stroke-chart-3',
  },
  cleaning: {
    icon: Sparkles,
    text: 'text-warning',
    tile: 'bg-warning/15 text-warning ring-warning/25',
    edge: 'bg-warning',
    border: 'border-warning/30',
    glow: 'hover:shadow-warning/15',
    blob: 'bg-warning/10',
    ring: 'ring-warning/60',
    flash: 'border-warning',
    stroke: 'stroke-warning',
  },
  blocked: {
    icon: Lock,
    text: 'text-destructive',
    tile: 'bg-destructive/10 text-destructive ring-destructive/20',
    edge: 'bg-destructive',
    border: 'border-destructive/25',
    glow: 'hover:shadow-destructive/15',
    blob: 'bg-destructive/10',
    ring: 'ring-destructive/60',
    flash: 'border-destructive',
    stroke: 'stroke-destructive',
  },
  vacant: {
    icon: BedSingle,
    text: 'text-success',
    tile: 'bg-success/10 text-success ring-success/20',
    edge: 'bg-success',
    border: 'border-success/25',
    glow: 'hover:shadow-success/15',
    blob: 'bg-success/10',
    ring: 'ring-success/60',
    flash: 'border-success',
    stroke: 'stroke-success',
  },
};

export const STATUS_BAR: Record<BoardStatus, string> = {
  occupied: 'bg-info',
  reserved: 'bg-chart-3',
  cleaning: 'bg-warning',
  blocked: 'bg-destructive',
  vacant: 'bg-success',
};

const PAYER_ICON: Record<string, LucideIcon> = {
  insurance: ShieldCheck,
  corporate: Building2,
  government: Landmark,
  ngo: HandHeart,
};

export function payerIcon(kind: string | null): LucideIcon {
  return (kind && PAYER_ICON[kind]) || ShieldCheck;
}

const KNOWN_BED_CLASSES = new Set(['general', 'semi_private', 'private', 'deluxe', 'icu', 'ccu', 'nicu', 'hdu', 'isolation']);

export function bedClassLabel(bedClass: string): string {
  return KNOWN_BED_CLASSES.has(bedClass) ? tr('ipd', `bed_class_${bedClass}`) : humanize(bedClass);
}

const AGE_KEY: Record<AgeUnit, string> = {
  days: 'age_days_short',
  months: 'age_months_short',
  years: 'age_years_short',
};

export function ageText(age: { value: number; unit: AgeUnit; approximate: boolean } | null): string | null {
  if (!age) return null;
  const text = tr('ipd', AGE_KEY[age.unit], { count: age.value });
  return age.approximate ? `~${text}` : text;
}
